import type { User } from '@/types/users';
import { defineStore } from 'pinia';
import { computed, reactive, toRaw, toRefs } from 'vue';
import {
  setLocalStorageItem,
  getLocalStorageItem,
  STORAGE_KEYS,
  removeLocalStorageItem,
} from '@/utils/localStorage';
import { identifyUser } from '@/plugins/gainsight';
import { clearDataDogUser, setDataDogUser } from '@/plugins/datadog';
import { identifyCustomerIo, resetCustomerIo } from '@/plugins/customerio';
import type { AxleProfile } from '@/types/teamworks';
import type { Impersonator } from '@/types/auth';
import type { ApiPermission } from '@/types/permissions';
import { cloneDeep } from 'lodash-es';
import { useGmApi } from '@/composables/useGmApi';
import { TEAMWORKS_APP_SLUGS } from '@teamworksdev/vuetify';
export const hasUser = () => !!getLocalStorageItem<User>(STORAGE_KEYS.USER);

interface UseUserStoreState {
  user: User | null;
  axleProfile: AxleProfile | null;
  impersonator: Impersonator | null;
  permissions: ApiPermission[] | null;
}

const DEFAULT_STATE: UseUserStoreState = {
  user: getLocalStorageItem<User>(STORAGE_KEYS.USER),
  axleProfile: null,
  impersonator: getLocalStorageItem<Impersonator>(STORAGE_KEYS.IMPERSONATOR),
  permissions: null,
};

function cloneDefaultState() {
  return structuredClone(DEFAULT_STATE);
}

export const useUserStore = defineStore('user', () => {
  const GmApi = useGmApi();

  const state = reactive<UseUserStoreState>(cloneDefaultState());

  const setUser = (user: User) => {
    // Clone to break readonly reactive proxy chain from Vue Query data
    state.user = cloneDeep(toRaw(user));
    setLocalStorageItem<User>(STORAGE_KEYS.USER, state.user);
    setDataDogUser(state.user.twrn);
  };

  const setIsSuperAdmin = (isSuperAdmin: boolean) => {
    if (state.user) {
      state.user.isSuperAdmin = isSuperAdmin;
    }
  };

  const unsetUser = () => {
    state.user = null;
    removeLocalStorageItem(STORAGE_KEYS.USER);
    clearDataDogUser();
    resetCustomerIo();
  };

  const setAppSwitcher = async () => {
    if (state.axleProfile !== null) return;

    try {
      state.axleProfile = await GmApi.getAxleProfile();
      identifyUser(state.user!, state.axleProfile.userTwrn, state.axleProfile.orgTwrn);
      if (state.user) identifyCustomerIo(state.user);
    } catch (error) {
      console.error('Failed to load app switcher:', error);
      // Allow the app to continue loading even if app switcher fails
      // This enables the app to load if user is already logged in
    }
  };

  const setImpersonator = (impersonator: Impersonator) => {
    state.impersonator = impersonator;
    setLocalStorageItem<Impersonator>(STORAGE_KEYS.IMPERSONATOR, impersonator);
  };

  const unsetImpersonator = () => {
    state.impersonator = null;
    removeLocalStorageItem(STORAGE_KEYS.IMPERSONATOR);
  };

  const hasUserAcceptedTermsOfUse = () => {
    // TODO: Implement this
    return true;
  };

  const hasActiveWalletProfile = computed(() => {
    return state.axleProfile?.accountApps[TEAMWORKS_APP_SLUGS.WALLET]?.hasActiveProfile ?? false;
  });

  const setPermissions = (permissions: ApiPermission[]) => {
    // Clone to break readonly reactive proxy chain from Vue Query data
    state.permissions = cloneDeep(toRaw(permissions));
  };

  const unsetPermissions = () => {
    state.permissions = null;
  };

  const $reset = () => {
    Object.assign(state, cloneDefaultState());
  };

  return {
    ...toRefs(state),
    setUser,
    unsetUser,
    setAppSwitcher,
    setImpersonator,
    unsetImpersonator,
    setPermissions,
    setIsSuperAdmin,
    unsetPermissions,
    hasUserAcceptedTermsOfUse,
    hasActiveWalletProfile,
    $reset,
  };
});
